import { isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChatContext } from '../../../../Chat.hook.ts';
import './ChatInfoProgrammedMessages.styles.scss';
import FollowUp from './components/FollowUp.component.tsx';

export default function ChatInfoProgrammedMessages() {
  const { followUps, setSelectedFollowUp } = useChatContext();
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const itemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const [isActive, setIsActive] = useState(false);
  const { t } = useTranslation(['chat/chat']);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isActive || isEmpty(followUps)) return;

      e.stopPropagation();

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, followUps.length - 1));
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      }

      if (e.key === 'Enter' && followUps.length > 0) {
        e.preventDefault();
        const selectedFollowUp = followUps[selectedIndex];
        if (!selectedFollowUp) return;

        setSelectedFollowUp({
          id: selectedFollowUp.id,
          trigger_date: new Date(
            selectedFollowUp.followUp?.triggerDate || Date.now(),
          ),
          consumed: selectedFollowUp.followUp?.consumed || false,
          content: {
            text: selectedFollowUp.content?.text || '',
            attachments: selectedFollowUp.content?.attachments || undefined,
          },
        });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [followUps, selectedIndex, isActive]);

  // Ensure the selected note is always visible
  useEffect(() => {
    itemsRef.current[selectedIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });
  }, [selectedIndex]);

  // Reset the index if the list of notes changes
  useEffect(() => {
    setSelectedIndex((prev) => Math.min(prev, followUps.length - 1));
  }, [followUps.length]);

  return (
    <>
      {isEmpty(followUps) ? (
        <div className="ChatInfoProgrammedMessages__Content">
          <p>{t('programmedMessages.noFollowUps')}</p>
        </div>
      ) : (
        <div
          className="ChatInfoProgrammedMessages__FollowUps"
          onMouseEnter={() => setIsActive(true)}
          onMouseLeave={() => setIsActive(false)}
        >
          {followUps.map((followUp, index) => (
            <FollowUp
              ref={(el) => {
                if (el) itemsRef.current[index] = el;
              }}
              index={index}
              selectedIndex={selectedIndex}
              key={followUp.id}
              {...followUp}
            />
          ))}
        </div>
      )}
    </>
  );
}
